/* eslint-disable @typescript-eslint/no-use-before-define */
import { DeleteOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Checkbox, Form, Input, message, Modal, Popconfirm, Select, Space, Switch, Table, Upload } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  exportNounCsv,
  fetchKeyWordList,
  generatePronunciation,
  importNounCsv,
  saveKeyWordList,
  setKeyWordListStatus,
  updateKeyWordList,
} from '@/api/subtitles';
import css from './style.module.less';

const MAX_TERMS = 500;
const MAX_TEXT_LEN = 100;
const LANGUAGES = ['zh', 'en', 'ja'];
// 危险文本探测(HTML 标签样)：命中即视为非法，前端直接拦截，不发后端(避免 <script> 触发 WAF net::ERR_FAILED)
const ILLEGAL_TEXT_RE = /<\s*\/?\s*[a-zA-Z]/;
const hasIllegalText = (text: string): boolean => ILLEGAL_TEXT_RE.test(text || '');

interface PairRow extends API.NounPair {
  index: number;
}

const VoiceDictionary: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [nounId, setNounId] = useState<string | undefined>(undefined);
  const [pairs, setPairs] = useState<API.NounPair[]>([]);
  const [status, setStatus] = useState<'ON' | 'OFF'>('OFF');

  const [pageLoading, setPageLoading] = useState(true);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  // 记录上次自动生成的读音，避免覆盖用户手改的值
  const lastAutoPronRef = useRef<string | null>(null);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [autoGenerate, setAutoGenerate] = useState(true);

  // 读音为空且与词相等的历史数据后端已归一；这里直接信任 noun_pair_list
  const buildFromLegacy = (res: API.FetchKeyWordListResult): API.NounPair[] => {
    const nouns = res.noun_list || [];
    const prons = res.pronoun_list || [];
    return nouns
      .filter((it) => it !== '')
      .map((text, i) => ({
        text,
        pronunciation: prons[i] && prons[i] !== text ? prons[i] : '',
        language: '',
      }));
  };

  const loadList = async (silent = false) => {
    if (!silent) setPageLoading(true);
    try {
      const { data } = await fetchKeyWordList();
      const res = data.data;
      if (res?.noun_id) {
        setNounId(res.noun_id);
        setStatus(res.frequency_status);
        setPairs(res.noun_pair_list?.length ? res.noun_pair_list : buildFromLegacy(res));
        return;
      }
      // 懒创建：用户首次进入还没有词表
      await saveKeyWordList({ noun_list: [''] });
      const { data: d2 } = await fetchKeyWordList();
      const res2 = d2.data;
      setNounId(res2?.noun_id);
      setStatus(res2?.frequency_status || 'OFF');
      setPairs(res2?.noun_pair_list?.length ? res2.noun_pair_list : buildFromLegacy(res2));
    } catch (e) {
      message.error(t('voiceDict.fetchFail'));
    } finally {
      if (!silent) setPageLoading(false);
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 全量替换落库（单一词表）
  const persist = async (newPairs: API.NounPair[], successMsg: string) => {
    if (!nounId) return false;
    setSaving(true);
    try {
      await updateKeyWordList({ noun_id: nounId, noun_pair_list: newPairs });
      setPairs(newPairs);
      message.success(successMsg);
      await loadList(true);
      return true;
    } catch (e) {
      message.error(t('voiceDict.saveFail'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (checked: boolean) => {
    if (!nounId) return;
    const next = checked ? 'ON' : 'OFF';
    setSwitchLoading(true);
    try {
      await setKeyWordListStatus({ noun_id: nounId, frequency_status_enum: next });
      setStatus(next);
    } catch (e) {
      message.error(t('voiceDict.saveFail'));
    } finally {
      setSwitchLoading(false);
    }
  };

  const langLabel = (code: string) => {
    if (code === 'zh') return t('voiceDict.langZh');
    if (code === 'en') return t('voiceDict.langEn');
    if (code === 'ja') return t('voiceDict.langJa');
    return '-';
  };

  const tableData: PairRow[] = pairs.map((p, index) => ({ ...p, index }));

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (v: number) => v + 1,
    },
    {
      title: t('voiceDict.colText'),
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
    },
    {
      title: t('voiceDict.colPronunciation'),
      dataIndex: 'pronunciation',
      key: 'pronunciation',
      render: (v: string) => v || '-',
    },
    {
      title: t('voiceDict.colLanguage'),
      dataIndex: 'language',
      key: 'language',
      width: 120,
      render: (v: string) => langLabel(v),
    },
    {
      title: t('voiceDict.colActions'),
      key: 'action',
      width: 160,
      render: (_: unknown, record: PairRow) => (
        <Space size="middle">
          <a onClick={() => handleEdit(record)}>{t('voiceDict.edit')}</a>
          <Popconfirm
            title={t('common.deleteWarn')}
            onConfirm={() => handleDelete(record.index)}
            okText={t('voiceDict.confirmDeleteBtn')}
            cancelText={t('common.cancel')}
          >
            <a style={{ color: 'red' }}>{t('voiceDict.delete')}</a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    if (pairs.length >= MAX_TERMS) {
      message.warning(t('voiceDict.maxTerms'));
      return;
    }
    form.resetFields();
    setEditIndex(null);
    lastAutoPronRef.current = null;
    setIsModalOpen(true);
  };

  const handleEdit = (record: PairRow) => {
    form.setFieldsValue({
      text: record.text,
      pronunciation: record.pronunciation,
      language: record.language || undefined,
    });
    setEditIndex(record.index);
    lastAutoPronRef.current = null;
    setIsModalOpen(true);
  };

  const handleDelete = async (index: number) => {
    const newPairs = pairs.filter((_, i) => i !== index);
    await persist(newPairs, t('voiceDict.deleteSuccess'));
  };

  const handleGenerate = async () => {
    const text: string = form.getFieldValue('text');
    const language: string = form.getFieldValue('language');
    if (!text || !text.trim() || !language) {
      message.warning(t('voiceDict.generateNeedTextLang'));
      return;
    }
    if (hasIllegalText(text)) {
      message.error(t('voiceDict.textIllegalChar'));
      return;
    }
    setGenerating(true);
    try {
      const { data } = await generatePronunciation({ text: text.trim(), language });
      const pron = data.data?.pronunciation || '';
      if (pron) {
        form.setFieldValue('pronunciation', pron);
        lastAutoPronRef.current = pron;
      } else {
        message.info(t('voiceDict.generateEmpty'));
      }
    } catch (e) {
      message.error(t('voiceDict.generateFail'));
    } finally {
      setGenerating(false);
    }
  };

  // 仅日语：文本失焦或选中日语时自动生成读音（读音框为空、或仍是上次自动值时才回填，不覆盖手改）
  const autoFillPronunciation = async () => {
    const language: string = form.getFieldValue('language');
    const text: string = (form.getFieldValue('text') || '').trim();
    if (language !== 'ja' || !text) return;
    // 含非法字符不自动生成，否则会把 <script> 发给后端触发 WAF net::ERR_FAILED；校验/保存会给明确提示
    if (hasIllegalText(text)) return;
    const curPron: string = (form.getFieldValue('pronunciation') || '').trim();
    if (curPron && curPron !== lastAutoPronRef.current) return;
    setGenerating(true);
    try {
      const { data } = await generatePronunciation({ text, language });
      const pron = data.data?.pronunciation || '';
      // 生成期间文本可能又变了，仍一致才回填
      if (pron && (form.getFieldValue('text') || '').trim() === text) {
        form.setFieldValue('pronunciation', pron);
        lastAutoPronRef.current = pron;
      }
    } catch (e) {
      // 自动生成失败静默，用户可点按钮手动重试
    } finally {
      setGenerating(false);
    }
  };

  const handleTextBlur = () => autoFillPronunciation();
  const handleLanguageChange = (value?: string) => {
    if (value === 'ja') autoFillPronunciation();
  };

  const handleModalOk = () => {
    form.validateFields().then(async (values) => {
      const text = (values.text || '').trim();
      const language = values.language || '';
      // KW-07：拒绝含 HTML 标签样内容（兜底；表单校验一般已在此之前拦下并内联提示）
      if (hasIllegalText(text)) {
        message.error(t('voiceDict.textIllegalChar'));
        return;
      }
      // KW-08：按（文本+语言）查重，避免重复添加
      const isDup = pairs.some(
        (p, i) => i !== editIndex && (p.text || '').trim() === text && (p.language || '') === language,
      );
      if (isDup) {
        message.warning(t('voiceDict.duplicateTerm'));
        return;
      }
      let pronunciation: string;
      if (language === 'ja') {
        pronunciation = (values.pronunciation || '').trim();
        if (!pronunciation) {
          // 兜底：日语但读音为空，保存时补生成一次
          setGenerating(true);
          try {
            const { data } = await generatePronunciation({ text, language });
            pronunciation = data.data?.pronunciation || text;
          } catch (e) {
            pronunciation = text;
          } finally {
            setGenerating(false);
          }
        }
      } else {
        pronunciation = text; // 中/英/未选语言：读音直接取原文
      }
      const pair: API.NounPair = { text, pronunciation, language };
      const newPairs = [...pairs];
      if (editIndex !== null) {
        newPairs[editIndex] = pair;
      } else {
        if (newPairs.length >= MAX_TERMS) {
          message.warning(t('voiceDict.maxTerms'));
          return;
        }
        newPairs.push(pair);
      }
      const ok = await persist(newPairs, t('voiceDict.saveSuccess'));
      if (ok) {
        setIsModalOpen(false);
        form.resetFields();
        lastAutoPronRef.current = null;
      }
    });
  };

  const handleExport = async () => {
    if (!pairs.length) {
      message.info(t('voiceDict.exportEmpty'));
      return;
    }
    try {
      const resp = await exportNounCsv();
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'voice-dictionary.csv';
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      message.error(t('voiceDict.exportFail'));
    }
  };

  const handleClearAll = async () => {
    await persist([], t('voiceDict.clearSuccess'));
  };

  const importErrorMessage = (err: any) => {
    const msg: string = err?.response?.data?.message || '';
    if (msg.includes('illegal characters')) return t('voiceDict.importIllegalChar');
    if (msg.includes('exceed')) return t('voiceDict.importTooMany');
    if (msg.includes('header') || err?.response?.status === 400) return t('voiceDict.importInvalid');
    return t('voiceDict.importFail');
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    setUploadLoading(true);
    try {
      const { data } = await importNounCsv({ file: selectedFile, auto_generate_pronunciation: autoGenerate });
      const r: any = data?.data;
      // 后端返回计数则展示"新增/更新/跳过空行"(KW-14/16);旧后端无此结构时回退为通用提示
      if (r && typeof r.imported === 'number') {
        message.success(
          t('voiceDict.importResult', {
            imported: r.imported,
            updated: r.updated || 0,
            skipped: r.skipped_empty || 0,
          }),
        );
      } else {
        message.success(t('voiceDict.importSuccess'));
      }
      setIsUploadOpen(false);
      setSelectedFile(null);
      await loadList(true);
    } catch (e) {
      message.error(importErrorMessage(e));
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className={css.pageContent}>
      <div className={css.pageHeader}>
        <div className={css.title}>
          {t('voiceDict.title')}
          <Switch
            className={css.switch}
            checked={status === 'ON'}
            loading={switchLoading}
            disabled={!nounId}
            onChange={handleToggleStatus}
          />
          <span className={css.countHint}>
            {t('voiceDict.termCount', { current: pairs.length, max: MAX_TERMS })}
          </span>
        </div>
        <Space className={css.buttonGroup}>
          <Button type="primary" icon={<PlusOutlined />} disabled={!nounId} onClick={handleAdd}>
            {t('voiceDict.add')}
          </Button>
          <Button icon={<UploadOutlined />} disabled={!nounId} onClick={() => setIsUploadOpen(true)}>
            {t('voiceDict.import')}
          </Button>
          <Button icon={<DownloadOutlined />} disabled={!nounId} onClick={handleExport}>
            {t('voiceDict.export')}
          </Button>
          <Popconfirm
            title={t('voiceDict.clearAllConfirm')}
            onConfirm={handleClearAll}
            okText={t('voiceDict.confirmDeleteBtn')}
            cancelText={t('common.cancel')}
            okButtonProps={{ danger: true }}
            disabled={!nounId || pairs.length === 0}
          >
            <Button danger icon={<DeleteOutlined />} disabled={!nounId || pairs.length === 0}>
              {t('voiceDict.clearAll')}
            </Button>
          </Popconfirm>
        </Space>
      </div>

      <Table
        className={css.contentTable}
        rowKey="index"
        columns={columns}
        dataSource={tableData}
        loading={pageLoading}
        pagination={{ pageSize: 10, showSizeChanger: false }}
      />

      <Modal
        title={editIndex !== null ? t('voiceDict.edit') : t('voiceDict.add')}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            {t('common.cancel')}
          </Button>,
          <Button key="submit" type="primary" loading={saving} onClick={handleModalOk}>
            {t('common.confirm')}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="text"
            label={t('voiceDict.colText')}
            rules={[
              { required: true, message: t('voiceDict.textRequired') },
              { max: MAX_TEXT_LEN, message: t('voiceDict.textMax') },
              {
                validator: (_, v) =>
                  hasIllegalText(v) ? Promise.reject(new Error(t('voiceDict.textIllegalChar'))) : Promise.resolve(),
              },
            ]}
          >
            <Input
              placeholder={t('voiceDict.textPlaceholder')}
              maxLength={MAX_TEXT_LEN}
              showCount
              onBlur={handleTextBlur}
            />
          </Form.Item>

          <Form.Item name="language" label={t('voiceDict.colLanguage')}>
            <Select
              allowClear
              onChange={handleLanguageChange}
              placeholder={t('voiceDict.languagePlaceholder')}
              options={LANGUAGES.map((code) => ({ value: code, label: langLabel(code) }))}
            />
          </Form.Item>

          {/* 读音框 + 生成按钮仅日语显示；中/英保存时直接取原文 */}
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.language !== cur.language}>
            {({ getFieldValue }) =>
              getFieldValue('language') === 'ja' ? (
                <Form.Item label={t('voiceDict.colPronunciation')}>
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="pronunciation" noStyle>
                      <Input placeholder={t('voiceDict.pronunciationPlaceholder')} />
                    </Form.Item>
                    <Button loading={generating} onClick={handleGenerate}>
                      {t('voiceDict.generate')}
                    </Button>
                  </Space.Compact>
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('voiceDict.import')}
        open={isUploadOpen}
        onCancel={() => {
          setIsUploadOpen(false);
          setSelectedFile(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsUploadOpen(false);
              setSelectedFile(null);
            }}
          >
            {t('common.cancel')}
          </Button>,
          <Button key="upload" type="primary" disabled={!selectedFile} loading={uploadLoading} onClick={handleConfirmUpload}>
            {t('common.confirm')}
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>{t('voiceDict.importHint')}</div>
          <Checkbox checked={autoGenerate} onChange={(e) => setAutoGenerate(e.target.checked)}>
            {t('voiceDict.autoGenerate')}
          </Checkbox>
          <Upload.Dragger
            accept=".csv"
            showUploadList={true}
            maxCount={1}
            fileList={
              selectedFile ? [{ uid: '1', name: selectedFile.name, status: 'done', originFileObj: selectedFile as any }] : []
            }
            beforeUpload={(file) => {
              setSelectedFile(file);
              return false;
            }}
            onRemove={() => setSelectedFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">{t('voiceDict.uploadDragText')}</p>
            <p className="ant-upload-hint">{t('voiceDict.uploadFormatHint')}</p>
          </Upload.Dragger>
        </Space>
      </Modal>
    </div>
  );
};

export default VoiceDictionary;
